import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { useCustomerStore } from "../../../store/customerStore";
import { useEmployeeStore } from "../../../store/employeeStore";
import { IconTextButton } from "../../../components/ButtonComponents";
import { Save, X, MapPin, Search, Plus, UserCircle2 } from "lucide-react";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { Frequency, VisitTemplate } from "../../../types/visit";
import { Employee } from "../../../types/employee";
import { CustomNumberInput } from "../../../components/designer/CustomNumberInput";
import { CustomSelectInput } from "../../../components/CustomSelectInput";

const DAYS = [
    { label: 'Pazartesi', value: 1 }, { label: 'Salı', value: 2 },
    { label: 'Çarşamba', value: 3 }, { label: 'Perşembe', value: 4 },
    { label: 'Cuma', value: 5 }, { label: 'Cumartesi', value: 6 },
    { label: 'Pazar', value: 7 }
];

const FREQUENCIES = [
    { label: 'Her Hafta', value: { freq: 'WEEKLY', interval: 1 } },
    { label: '2 Haftada Bir', value: { freq: 'WEEKLY', interval: 2 } },
    { label: '3 Haftada Bir', value: { freq: 'WEEKLY', interval: 3 } },
    { label: 'Ayda Bir', value: { freq: 'MONTHLY', interval: 1 } },
];

interface Props {
    isOpen: boolean;
    onClose: () => void;
    employeeId: number | null;
    initialCustomer?: any;
    initialData?: VisitTemplate | null; // Düzenleme için eklendi
    onSave: (data: Partial<VisitTemplate>) => void;
}

export const VisitTemplateDialog: React.FC<Props> = ({ isOpen, onClose, employeeId, initialCustomer, initialData, onSave }) => {
    const customerStore = useCustomerStore();
    const employeeStore = useEmployeeStore();

    const customerSelect = useSelectDialog<any>();
    const employeeSelect = useSelectDialog<Employee>();

    const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

    const [formData, setFormData] = useState({
        accountId: null as number | null,
        addressId: null as number | null,
        dayOfWeek: 1,
        visitOrder: 1,
        frequency: 'WEEKLY' as Frequency,
        interval: 1,
        dayOfMonth: 1
    });

    // Modal açıldığında form değerlerini yükle
    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                // Önce varsayılan veya elimizdeki sığ veriyi set edelim
                setSelectedCustomer(initialData.account || { id: initialData.accountId, name: "Müşteri Yükleniyor..." });
                setFormData({
                    accountId: initialData.accountId,
                    addressId: initialData.addressId || null,
                    dayOfWeek: initialData.dayOfWeek || 1,
                    visitOrder: initialData.visitOrder || 1,
                    frequency: initialData.frequency || 'WEEKLY',
                    interval: initialData.interval || 1,
                    dayOfMonth: initialData.dayOfMonth || 1
                });

                // Adreslerin gelmesi için müşterinin tam detayını çekelim
                const fetchCustomer = async () => {
                    try {
                        const res = await customerStore.fetchDataDynamic({
                            filter: { logic: "and", filters: [{ field: "id", operator: "eq", value: initialData.accountId }] }
                        });
                        if (res.items && res.items.length > 0) {
                            setSelectedCustomer(res.items[0]);
                        }
                    } catch (e) {
                        console.error("Müşteri detayları çekilemedi", e);
                    }
                };
                fetchCustomer();

            } else if (initialCustomer) {
                setSelectedCustomer(initialCustomer);
                setFormData(prev => ({
                    ...prev,
                    accountId: initialCustomer.id,
                    addressId: initialCustomer.addresses?.[0]?.id || null
                }));
            }
        }
    }, [isOpen, initialCustomer, initialData]);

    const openCustomerSelect = async () => {
        const res = await customerStore.fetchDataDynamic({ pageIndex: 1, pageSize: 10 });
        const selected = await customerSelect.open({
            title: "Müşteri Seçin",
            data: res?.items || [],
            searchFields: [{ field: "name", operator: "contains" }],
            columns: [
                { field: "name", header: "Müşteri Adı" },
                { field: "city", header: "Şehir" }
            ],
            onFilter: async (p) => {
                const r = await customerStore.fetchDataDynamic({
                    pageIndex: p.pageIndex,
                    pageSize: p.pageSize,
                    filter: p.filter
                });
                return { data: r.items, total: r.total };
            }
        });

        if (selected) {
            setSelectedCustomer(selected);
            setFormData(prev => ({
                ...prev,
                accountId: selected.id,
                addressId: selected.addresses?.[0]?.id || null
            }));
        }
    };

    const openEmployeeSelect = async () => {
        const res = await employeeStore.fetchDataDynamic({ pageIndex: 1, pageSize: 10 });
        const selected = await employeeSelect.open({
            title: "Plasiyer Seçin",
            data: res?.items || [],
            searchFields: [
                { field: "firstName", operator: "contains" },
                { field: "lastName", operator: "contains" }
            ],
            columns: [
                { field: "firstName", header: "Ad" },
                { field: "lastName", header: "Soyad" }
            ],
            onFilter: async (p) => {
                const r = await employeeStore.fetchDataDynamic({
                    pageIndex: p.pageIndex,
                    pageSize: p.pageSize,
                    filter: p.filter
                });
                return { data: r.items, total: r.total };
            }
        });

        if (selected) {
            setSelectedEmployee(selected);
        }
    };

    const handleSave = () => {
        const targetEmployeeId = employeeId || selectedEmployee?.id;
        if (!formData.accountId || !targetEmployeeId) return;

        onSave({
            id: initialData?.id,
            ...formData,
            employeeId: targetEmployeeId,
            accountId: formData.accountId ?? undefined,
            addressId: formData.addressId ?? undefined,
            dayOfMonth: formData.frequency === 'MONTHLY' ? 1 : undefined
        });

        // Temizlik
        if (!initialCustomer && !initialData) {
            setSelectedCustomer(null);
            setFormData({
                accountId: null,
                addressId: null,
                dayOfWeek: 1,
                visitOrder: 1,
                frequency: 'WEEKLY',
                interval: 1,
                dayOfMonth: 1
            });
        }
        setSelectedEmployee(null);
        onClose();
    };

    const handleHide = () => {
        // Modal kapanırken temizle
        if (!initialCustomer) {
            setSelectedCustomer(null);
            setFormData({
                accountId: null,
                addressId: null,
                dayOfWeek: 1,
                visitOrder: 1,
                frequency: 'WEEKLY',
                interval: 1,
                dayOfMonth: 1
            });
        }
        setSelectedEmployee(null);
        onClose();
    };

    const targetEmployeeId = employeeId || selectedEmployee?.id;
    const isSaveDisabled = !formData.accountId || !targetEmployeeId;

    return (
        <Dialog
            header={<div className="flex items-center gap-2"><Plus size={20} className="text-primary" /> <span>Yeni Rota Atama</span></div>}
            visible={isOpen}
            onHide={handleHide}
            className="w-full md:w-[450px]"
            breakpoints={{ '960px': '75vw', '641px': '95vw' }}
            footer={
                <div className="flex justify-end gap-2 p-3">
                    <IconTextButton icon={X} onClick={handleHide} className="bg-bgAlt hover:bg-borderColor text-text">İptal</IconTextButton>
                    <IconTextButton icon={Save} onClick={handleSave} disabled={isSaveDisabled} className="bg-primary hover:bg-primaryHover text-white disabled:opacity-50">Kaydet</IconTextButton>
                </div>
            }
        >
            <div className="flex flex-col gap-5 py-4">
                {/* Plasiyer Seçimi (Eğer dışarıdan employeeId gelmediyse) */}
                {!employeeId && (
                    <CustomSelectInput
                        label={<><UserCircle2 size={14} className="text-primary" /> Plasiyer Seçimi</>}
                        value={selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : ""}
                        placeholder="Plasiyer bul..."
                        icon={UserCircle2}
                        onClick={openEmployeeSelect}
                        onClear={() => setSelectedEmployee(null)}
                    />
                )}

                {/* Müşteri Seçimi */}
                <CustomSelectInput
                    label={<>Müşteri Seçimi</>}
                    value={selectedCustomer?.name || ""}
                    placeholder="Müşteri bul..."
                    icon={Search}
                    onClick={!initialCustomer ? openCustomerSelect : () => {}}
                    onClear={!initialCustomer ? () => {
                        setSelectedCustomer(null);
                        setFormData(prev => ({ ...prev, accountId: null, addressId: null }));
                    } : undefined}
                    disabled={!!initialCustomer}
                />

                {/* Adres Seçimi */}
                {selectedCustomer?.addresses?.length > 0 && (
                    <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-top-1">
                        <label className="text-xs font-black uppercase text-primary flex items-center gap-2">
                            <MapPin size={14} /> Ziyaret Adresi
                        </label>
                        <Dropdown
                            value={formData.addressId}
                            options={selectedCustomer.addresses}
                            optionLabel="title"
                            optionValue="id"
                            onChange={(e) => setFormData({ ...formData, addressId: e.value })}
                            placeholder="Adres Seçiniz"
                            className="w-full"
                        />
                    </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                        <label className="text-xs font-black uppercase text-text/60">Ziyaret Günü</label>
                        <Dropdown
                            value={formData.dayOfWeek}
                            options={DAYS}
                            onChange={(e) => setFormData({ ...formData, dayOfWeek: e.value })}
                            className="w-full bg-inputBg border-borderColor text-inputText"
                            disabled={formData.frequency === 'MONTHLY'}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-xs font-black uppercase text-text/60">Ziyaret Sırası</label>
                        <CustomNumberInput
                            value={formData.visitOrder}
                            onChange={(val) => setFormData({ ...formData, visitOrder: val || 1 })}
                            min={1}
                            className="w-full"
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <label className="text-xs font-black uppercase text-text/60">Tekrar Periyodu</label>
                    <Dropdown
                        value={{ freq: formData.frequency, interval: formData.interval }}
                        options={FREQUENCIES}
                        onChange={(e) => setFormData({
                            ...formData,
                            frequency: e.value.freq,
                            interval: e.value.interval
                        })}
                        optionLabel="label"
                        className="w-full bg-inputBg border-borderColor text-inputText"
                    />
                </div>
            </div>
            {customerSelect.modal}
            {employeeSelect.modal}
        </Dialog>
    );
};